import { AxiosError, AxiosResponse } from 'axios';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { ListErrors } from 'src/components/common/Errors/list';
import { UiToast } from 'src/components/UI/toasts';
import { CommonApiResponseType, PreparedApiErrorsType } from 'src/models/BaseApi';
import {
  convertPreparedApiErrorsToFormErrors,
  convertRawApiErrorResponseToPreparedApiErrors,
  getErrorTextFromPreparedApiResponse,
  PreparedFormErrorsType,
} from 'src/utils/errors';
import { isHttpResponseValid } from 'src/utils/httpResponse';
import { showModal } from 'src/store/commonPart/slice';
import { useTHelper } from 'src/utils/i18n';
import { SafeAnyType } from '../safeAny';
import { PromiseReturnType } from '../types';

export class ApiError {
  readonly name: string;

  readonly message: string;

  readonly errors: PreparedApiErrorsType;

  readonly rawResponse: CommonApiResponseType;

  readonly errorMessages: string[];

  readonly errorsByProperty: PreparedFormErrorsType;

  readonly errorsByPropertyNew: PreparedFormErrorsType;

  constructor(message: string, response: CommonApiResponseType) {
    this.name = 'ApiError';
    this.message = message;
    this.rawResponse = response;
    this.errors = convertRawApiErrorResponseToPreparedApiErrors(response);
    this.errorMessages = getErrorTextFromPreparedApiResponse(this.errors);
    this.errorsByProperty = convertPreparedApiErrorsToFormErrors(this.errors);
    this.errorsByPropertyNew = convertPreparedApiErrorsToFormErrors(this.errors, undefined, true);

    // 404 http code or something similar
    if (this.errorMessages.length === 0) {
      this.errorMessages.push(this.message);
    }

    if (this.errorsByProperty.length === 0) {
      this.errorsByProperty.push({ key: '', message: this.message });
    }
  }
}

type FNPropsType = <F extends (data: never) => SafeAnyType>(
  apiFunction: F,
  data?: Parameters<F>[0],
  options?: {
    showErrors?: boolean; // shows always if not set false
    key?: string; // the custom unique key for functions with different parameters
  }
) => Promise<PromiseReturnType<F>>;

const hashCode = (string: string): string => {
  let hash = 0;

  for (let i = 0, len = string.length; i < len; i += 1) {
    const chr = string.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + chr;
    // eslint-disable-next-line no-bitwise
    hash |= 0; // Convert to 32bit integer
  }

  return hash.toString();
};

export const useRequest = (): {
  abort: (specificPromise?: Promise<SafeAnyType>) => void;
  apiCaller: FNPropsType;
} => {
  const dispatch = useDispatch();

  const tHelper = useTHelper('common.modals.attention');

  const map = useRef<Map<string, { controller: AbortController; promise: Promise<SafeAnyType> }>>(new Map());

  /**
   * search in map by the given promise
   * returns object key or null
   */
  const searchByPromise = useCallback(
    (searchValue: Promise<SafeAnyType>) =>
      [...map.current.entries()].find(([, value]) => value.promise === searchValue)?.[0] ?? null,
    []
  );

  const apiCaller: FNPropsType = useCallback(
    (apiFunction, apiArguments, options) => {
      // the unique name of every given function
      // if it is called the second time, it must be canceled
      const pathMapKey = options?.key ?? hashCode(apiFunction.name);

      if (map.current.has(pathMapKey)) {
        map.current.get(pathMapKey)?.controller.abort();
        map.current.delete(pathMapKey);
      }

      const controller = new AbortController();

      const promise: Promise<SafeAnyType> = new Promise((resolve, reject) => {
        apiFunction
          // (type never) is a tricky way
          .call(null, { ...(apiArguments || {}), signal: controller.signal } as never)
          .then((response: SafeAnyType) => {
            if (isHttpResponseValid(response)) {
              resolve(response as AxiosResponse['data']);
            } else {
              const error = new ApiError(`Invalid response "${apiFunction.name}"`, response);

              reject(error);

              if (options?.showErrors !== false) {
                UiToast.error(<ListErrors errorMessages={error.errorMessages} />);
              }
            }

            map.current.delete(pathMapKey);
          })
          .catch((errorResponse: AxiosError) => {
            // is this need to be returned?
            if (errorResponse.name !== 'CanceledError') {
              const error = new ApiError(errorResponse.message, errorResponse.response);

              reject(error);

              if (options?.showErrors !== false) {
                if (errorResponse instanceof SyntaxError) {
                  dispatch(
                    showModal({
                      header: tHelper('header'),
                      body: tHelper('body'),
                      buttonFooter: {
                        onlySuccessBtn: true,
                        successBtnText: tHelper('successBtnText'),
                      },
                    })
                  );
                } else {
                  UiToast.error(<ListErrors errorMessages={error.errorMessages} />);
                }
              }
            }
          });
      });

      map.current.set(pathMapKey, { controller, promise });

      return promise;
    },
    [dispatch, tHelper]
  );

  const abort = useCallback(
    (specificPromise?: Promise<SafeAnyType>) => {
      if (specificPromise) {
        const keyMap = searchByPromise(specificPromise);

        if (keyMap) {
          map.current.get(keyMap)?.controller.abort();
          map.current.delete(keyMap);
        }
      } else {
        // clear the map
        map.current.forEach((value) => {
          value.controller.abort();
        });
        map.current.clear();
      }
    },
    [searchByPromise]
  );

  useEffect(
    () => () => {
      abort();
    },
    [abort]
  );

  return { abort, apiCaller };
};
